import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, ExternalLink, History, Tag, UserPlus } from 'lucide-react';
import { api } from '../../services/api';
import GreetedContactButtons from '../components/GreetedContactButtons';
import Disclosure from '../components/Disclosure';
import NotesThread from '../components/NotesThread';
import OrderFiles from '../components/OrderFiles';
import PaymentPanel from '../components/PaymentPanel';
import QuoteBuilder from '../components/QuoteBuilder';
import SnoozePicker from '../components/SnoozePicker';
import StatusAdvance from '../components/StatusAdvance';
import {
  apiFieldToKey, formatAge, formatDateTime, formatMinutes, formatMoney, maskedPhone, phoneFacts, toLocalInput,
} from '../format';
import '../crm.css';

// The three the site is published in, which are the three a greeting exists in.

// Where the Telegram notification lands, so it has to stand alone on a phone:
// who is calling, what they read, what was promised, and one tap to reach them.
export default function CrmLeadPage() {
  const { t, i18n } = useTranslation('common');
  const { leadId } = useParams();
  const [meta, setMeta] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [draft, setDraft] = useState({ order_value: '', next_action: '', next_action_at: '', lost_reason: '' });
  // These fields save on blur, so the confirmation names the field it saved - a
  // generic "saved" would still be on screen from the previous edit.
  const [savedField, setSavedField] = useState(null);

  // The draft is filled from the lead once per lead, never on every reload: a
  // status change refetches the card, and refilling it would wipe a price the
  // operator is in the middle of typing.
  const filledFor = useRef(null);

  const load = useCallback(async () => {
    try {
      setDetail(await api.getCrmLead(leadId));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [leadId]);

  useEffect(() => {
    api.getCrmMeta().then(setMeta).catch((e) => setError(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!detail || filledFor.current === leadId) return;
    filledFor.current = leadId;
    setDraft({
      order_value: detail.lead.order_value ?? '',
      next_action: detail.lead.next_action || '',
      next_action_at: toLocalInput(detail.lead.next_action_at),
      lost_reason: detail.lead.lost_reason || '',
    });
  }, [detail, leadId]);

  const patch = async (body) => {
    let saved;
    try {
      saved = await api.patchCrmLead(leadId, body);
    } catch (e) {
      const problem = e?.response?.data?.detail;
      setSaveError(problem?.error ? t(`crm.errors.${problem.error}`) : String(problem || e.message));
      // The refused value stays in the input otherwise, looking saved.
      setDraft((current) => ({ ...current, next_action_at: toLocalInput(detail.lead.next_action_at) }));
      return;
    }
    setSaveError('');
    if ('status' in body) {
      // A status move can set or clear the next step on the server, so the inputs follow it.
      setDraft((current) => ({
        ...current,
        next_action: saved.next_action,
        next_action_at: toLocalInput(saved.next_action_at),
      }));
    }
    await load();
    setSavedField(Object.keys(body)[0]);
    setTimeout(() => setSavedField(null), 2000);
  };

  if (error) return <div className="crm-page"><div className="crm-error">{String(error)}</div></div>;
  if (!detail) return <div className="crm-page"><div className="spinner" /></div>;

  const { lead, client, client_lead_count: clientLeadCount, documents } = detail;
  const overdue = !lead.first_contacted_at && new Date(lead.callback_due_at) < new Date();
  const facts = phoneFacts(lead.phone, i18n.language);
  const comeBack = meta && meta.come_back_statuses.includes(lead.status);

  const clientSummary = client
    ? [t('crm.lead.enquiryCount', { count: clientLeadCount }), client.city].filter(Boolean).join(' · ')
    : t('crm.lead.noProfile');

  return (
    <div className="crm-page crm-detail crm-lead-page">
      <Link className="crm-back" to="/crm">
        <ArrowLeft size={15} />
        {t('crm.lead.backToQueue')}
      </Link>

      <header className="crm-hero">
        <div className="crm-hero-identity">
          <h1 className="crm-hero-name">{lead.name}</h1>
          <p className="crm-hero-contact">
            {lead.phone ? maskedPhone(lead.phone) : lead.email}
            {lead.phone?.is_landline && <span className="crm-chip">{t('crm.lead.landline')}</span>}
          </p>
          <div className="crm-chips">
            <span className="crm-chip">
              <Tag size={12} aria-hidden="true" />
              {t(`crm.source.${lead.source}`)}
            </span>
            {facts.map((fact) => (
              <span className="crm-chip" key={fact}>{fact}</span>
            ))}
            {lead.company && (
              <span className="crm-chip">
                <Building2 size={12} aria-hidden="true" />
                {lead.company}
              </span>
            )}
            {lead.partner && <span className="crm-chip">{lead.partner}</span>}
          </div>
        </div>
        <div className="crm-hero-actions">
          {savedField && (
            <span className="crm-saved" data-saved-field={savedField}>
              {t('crm.savedField', { field: t(`crm.lead.${apiFieldToKey(savedField)}`) })}
            </span>
          )}
          {meta && (
            <StatusAdvance
              statuses={meta.statuses}
              comeBackStatuses={meta.come_back_statuses}
              nextStatus={meta.next_status}
              value={lead.status}
              onMove={patch}
            />
          )}
          <button
            type="button"
            className={`crm-btn${lead.assignee_user_id ? '' : ' primary'}`}
            onClick={() => patch({ assign_to_me: !lead.assignee_user_id })}
          >
            <UserPlus size={15} />
            {lead.assignee_user_id ? t('crm.lead.assignedTo', { name: lead.assignee_name }) : t('crm.lead.takeIt')}
          </button>
        </div>
      </header>

      {saveError && <div className="crm-error">{saveError}</div>}

      <div className="crm-vitals">
        <div className={`crm-vital${overdue ? ' alarm' : ''}`}>
          <span className="crm-vital-label">
            {lead.first_contacted_at ? t('crm.lead.answeredIn') : t('crm.lead.waiting')}
          </span>
          <strong className="crm-vital-value">
            {lead.first_contacted_at
              ? formatMinutes(lead.minutes_to_first_contact, t)
              : formatAge(lead.created_at, t)}
          </strong>
          <span className="crm-vital-note">
            {lead.first_contacted_at
              ? formatDateTime(lead.first_contacted_at)
              : t('crm.lead.callBy', { when: formatDateTime(lead.callback_due_at) })}
          </span>
        </div>
        <div className={`crm-vital${lead.payment_state === 'unpaid' ? ' owing' : ''}`}>
          <span className="crm-vital-label">{t('crm.quote.total')}</span>
          <strong className="crm-vital-value">
            {lead.order_total === null ? '—' : formatMoney(lead.order_total, i18n.language)}
            {lead.order_total !== null && <span className="crm-vital-unit">{t('crm.currency')}</span>}
          </strong>
          <span className="crm-vital-note">
            <span className={`crm-pay-badge crm-pay-${lead.payment_state}`}>
              {t(`crm.payment.${lead.payment_state}`)}
            </span>
            {lead.amount_due !== null && lead.amount_due > 0 && (
              <> {t('crm.payment.due', { amount: formatMoney(lead.amount_due, i18n.language) })}</>
            )}
          </span>
        </div>
        <div className="crm-vital">
          <span className="crm-vital-label">{t('crm.lead.status')}</span>
          <strong className="crm-vital-value crm-vital-status">{t(`crm.status.${lead.status}`)}</strong>
          <span className="crm-vital-note">
            {lead.assignee_user_id ? lead.assignee_name : t('crm.lead.unassigned')}
          </span>
        </div>
      </div>

      <div className="crm-contact-dock">
        <GreetedContactButtons
          record={lead}
          fill={(id) => api.fillCrmLeadGreetingNames(id)}
          greetingScript={meta?.greeting_script}
          phone={lead.phone}
          email={lead.email}
        />
      </div>

      <div className="crm-detail-grid">
        <div className="crm-detail-main">
          <section className="crm-card" data-area="quote">
            {meta && (
              <QuoteBuilder
                services={meta.services}
                charsPerUnit={meta.chars_per_unit}
                items={lead.quote_items}
                quoteTotal={lead.quote_total}
                orderTotal={lead.order_total}
                source={lead.order_value_source}
                manualDraft={draft.order_value}
                priceListUrl={meta.price_list_url}
                onItemsChange={(items) => patch({ quote_items: items })}
                onManualDraftChange={(value) => setDraft({ ...draft, order_value: value })}
                onManualCommit={() =>
                  patch({ order_value: draft.order_value === '' ? null : Number(draft.order_value) })
                }
                onUseQuote={() => {
                  setDraft({ ...draft, order_value: '' });
                  patch({ order_value: null });
                }}
              />
            )}
            <PaymentPanel
              lead={lead}
              onPay={(amount) => api.addCrmLeadPayment(leadId, amount).then(load)}
              onRemovePayment={(paymentId) =>
                api.removeCrmLeadPayment(leadId, paymentId).then(load)
              }
            />
          </section>

          <OrderFiles lead={lead} onChanged={load} />

          <section className="crm-card" data-area="work">
            <h2>{t('crm.lead.work')}</h2>
            <div className="crm-field-grid">
              <label className="crm-field">
                <span className="crm-field-label">{t('crm.lead.nextAction')}</span>
                <input
                  className="crm-input"
                  value={draft.next_action}
                  onChange={(event) => setDraft({ ...draft, next_action: event.target.value })}
                  onBlur={() => patch({ next_action: draft.next_action })}
                  placeholder={t('crm.lead.nextActionHint')}
                />
              </label>
              <label className="crm-field">
                <span className="crm-field-label">{t('crm.lead.nextActionAt')}</span>
                <input
                  className="crm-input"
                  type="datetime-local"
                  value={draft.next_action_at}
                  onChange={(event) => setDraft({ ...draft, next_action_at: event.target.value })}
                  onBlur={() =>
                    patch({
                      next_action_at: draft.next_action_at ? new Date(draft.next_action_at).toISOString() : null,
                    })
                  }
                />
              </label>
            </div>
            <SnoozePicker
              value={draft.next_action_at}
              comeBack={comeBack}
              onPick={(when) => {
                setDraft({ ...draft, next_action_at: when ? toLocalInput(when.toISOString()) : '' });
                patch({ next_action_at: when ? when.toISOString() : null });
              }}
            />
            {meta && meta.reason_statuses.includes(lead.status) && (
              <label className="crm-field">
                <span className="crm-field-label">{t('crm.lead.lostReason')}</span>
                <input
                  className="crm-input"
                  value={draft.lost_reason}
                  onChange={(event) => setDraft({ ...draft, lost_reason: event.target.value })}
                  onBlur={() => patch({ lost_reason: draft.lost_reason })}
                  placeholder={t('crm.lead.lostReasonHint')}
                />
              </label>
            )}
          </section>

          <section className="crm-card" data-area="notes">
            <h2>{t('crm.notes.title')}</h2>
            <NotesThread notes={lead.notes} onAdd={(text) => api.addCrmLeadNote(leadId, text).then(load)} />
          </section>
        </div>

        <aside className="crm-detail-rail">
          <Disclosure area="client" title={t('crm.lead.client')} summary={clientSummary}>
            {client ? (
              <>
                <div className="crm-card-head">
                  <p className="crm-sub">
                    {t('crm.lead.enquiryCount', { count: clientLeadCount })}
                    {client.city && ` · ${client.city}`}
                  </p>
                  <Link className="crm-link" to={`/crm/clients/${client.id}`}>
                    {t('crm.lead.openProfile')}
                    <ExternalLink size={13} />
                  </Link>
                </div>
                {documents.length > 0 && (
                  <ul className="crm-doc-list">
                    {documents.map((doc) => (
                      <li key={doc.id}>
                        <span>{doc.original_filename}</span>
                        <span className="crm-cell-sub">
                          {doc.language_from || '?'} → {doc.language_to} · {doc.char_count ?? '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                <p className="crm-empty-line">{t('crm.lead.noProfile')}</p>
                <div className="crm-card-actions">
                  <button
                    type="button"
                    className="crm-btn"
                    onClick={() => api.linkCrmLeadToClient(leadId).then(load)}
                  >
                    <UserPlus size={15} />
                    {t('crm.lead.createProfile')}
                  </button>
                </div>
              </>
            )}
          </Disclosure>

          <section className="crm-card crm-quiet" data-area="origin">
            <h2>{t('crm.lead.origin')}</h2>
            <dl className="crm-defs">
              <dt>{t('crm.lead.formPage')}</dt>
              <dd className="crm-break">{lead.attribution.form_path || '—'}</dd>
              <dt>{t('crm.lead.landingPage')}</dt>
              <dd className="crm-break">{lead.attribution.landing_path || '—'}</dd>
              <dt>{t('crm.lead.referrer')}</dt>
              <dd className="crm-break">{lead.attribution.referrer || '—'}</dd>
              <dt>{t('crm.lead.campaign')}</dt>
              <dd className="crm-break">
                {[lead.attribution.utm_source, lead.attribution.utm_medium, lead.attribution.utm_campaign]
                  .filter(Boolean)
                  .join(' / ') || '—'}
              </dd>
            </dl>
            {lead.message && <p className="crm-quote-text">{lead.message}</p>}
          </section>

          <section className="crm-card crm-quiet" data-area="history">
            <h2>
              <History size={14} /> {t('crm.lead.history')}
            </h2>
            <ul className="crm-history">
              {lead.status_history.map((change, index) => (
                <li key={`${change.at}-${index}`}>
                  <span className={`crm-status-dot crm-status-${change.status}`} />
                  {t(`crm.status.${change.status}`)} · {formatDateTime(change.at)} · {change.by_name}
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
